import { useState } from "react";
import { Box, Text, Paper } from "@mantine/core";
import { Button, Badge } from "@xplatform/design-system";
import { IconArrowRight, IconUsers, IconRobot, IconZoomIn, IconZoomOut, IconZoomReset } from "@tabler/icons-react";
import type { FlowConfigViewModel, FlowStepViewModel, AgentExecutionViewModel } from "@models/doctype";
import { useTranslation } from "@hooks/useTranslation";
import { translateStepType } from "@utils/flowTypeTranslations";
import { StepEditDialog } from "../StepEditDialog/StepEditDialog";
import { AgentDetailModal } from "../AgentDetailModal";
import { EvaluationResults } from "../EvaluationResults";
import { useCanvasPanZoom } from "@hooks/flow/useCanvasPanZoom";
import { FlowInfoPanel } from "./FlowInfoPanel";
import "./FlowDiagram.scss";

interface FlowDiagramProps {
    flowConfig: FlowConfigViewModel;
    docTypeId: string;
    docTypeCode?: string;
    onActivate: () => void;
    onRefresh?: () => void;
}

export const FlowDiagram = ({
    flowConfig,
    docTypeId,
    docTypeCode,
    onActivate,
    onRefresh,
}: FlowDiagramProps) => {
    const { t } = useTranslation();
    const [selectedStep, setSelectedStep] = useState<FlowStepViewModel | null>(
        null,
    );
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogInitialTab, setDialogInitialTab] = useState<string | undefined>(
        undefined,
    );
    const [selectedAgent, setSelectedAgent] = useState<AgentExecutionViewModel | null>(null);
    const [selectedAgentStep, setSelectedAgentStep] = useState<FlowStepViewModel | null>(null);
    const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);

    const sortedSteps = [...flowConfig.steps].sort(
        (a, b) => a.stepOrder - b.stepOrder,
    );

    const handleStepClick = (step: FlowStepViewModel) => {
        setSelectedStep(step);
        setDialogInitialTab(undefined);
        setIsDialogOpen(true);
    };

    const handleAgentCardClick = (agent: AgentExecutionViewModel, step: FlowStepViewModel) => {
        setSelectedAgent(agent);
        setSelectedAgentStep(step);
        setIsAgentModalOpen(true);
    };

    const handleCloseAgentModal = () => {
        setIsAgentModalOpen(false);
        setSelectedAgent(null);
        setSelectedAgentStep(null);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedStep(null);
        setDialogInitialTab(undefined);
    };

    const {
        canvasRef,
        pan,
        zoom,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        handleZoomIn,
        handleZoomOut,
        handleZoomReset,
    } = useCanvasPanZoom();

    return (
        <Box className="flow-diagram">
            <FlowInfoPanel
                flowConfig={flowConfig}
                docTypeCode={docTypeCode}
                onActivate={onActivate}
            />

            <Box className="flow-diagram__pipeline">
                <Box className="flow-diagram__pipeline-header">
                    <Text fw={600} size="lg" className="flow-diagram__pipeline-title">
                        {t("admin.flowConfigs.labels.steps")}
                    </Text>
                    <Box className="flow-diagram__zoom-controls">
                        <Button
                            variant="subtle"
                            size="compact-xs"
                            onClick={handleZoomIn}
                            title="Zoom in"
                        >
                            <IconZoomIn size={18} />
                        </Button>
                        <Text size="xs" fw={500} className="flow-diagram__zoom-level">
                            {Math.round(zoom * 100)}%
                        </Text>
                        <Button
                            variant="subtle"
                            size="compact-xs"
                            onClick={handleZoomOut}
                            title="Zoom out"
                        >
                            <IconZoomOut size={18} />
                        </Button>
                        <Button
                            variant="subtle"
                            size="compact-xs"
                            onClick={handleZoomReset}
                            title="Reset view"
                        >
                            <IconZoomReset size={18} />
                        </Button>
                    </Box>
                </Box>
                <Box
                    ref={canvasRef}
                    className="flow-diagram__canvas"
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                >
                    <Box
                        className="flow-diagram__canvas-inner"
                        style={{
                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                            transformOrigin: "0 0",
                        }}
                    >
                        {sortedSteps.map((step, index) => {
                            const agents = step.llmStepConfig?.executions;
                            const hasAgents = agents && agents.length > 0;

                            return (
                                <Box key={step.stepOrder} className="flow-diagram__step-wrapper">
                                    <Paper
                                        className="flow-diagram__step-card"
                                        onClick={() => handleStepClick(step)}
                                    >
                                        <Box className="flow-diagram__step-header">
                                            <Badge size="lg" className="flow-diagram__step-number">
                                                {step.stepOrder}
                                            </Badge>
                                            <Text fw={600} size="md" className="flow-diagram__step-type">
                                                {translateStepType(step.stepType, t)}
                                            </Text>
                                        </Box>
                                        <Box className="flow-diagram__step-content">
                                            {step.llmStepConfig && (
                                                <>
                                                    <Box className="flow-diagram__step-info">
                                                        <Text size="xs" c="dimmed">
                                                            {t("admin.flowConfigs.labels.provider")}
                                                        </Text>
                                                        <Text size="sm">{step.llmStepConfig.provider}</Text>
                                                    </Box>
                                                    <Box className="flow-diagram__step-info">
                                                        <Text size="xs" c="dimmed">
                                                            Model
                                                        </Text>
                                                        <Text size="sm">{step.llmStepConfig.modelName}</Text>
                                                    </Box>
                                                </>
                                            )}
                                            {step.ocrStepConfig && (
                                                <>
                                                    <Box className="flow-diagram__step-info">
                                                        <Text size="xs" c="dimmed">
                                                            {t("admin.flowConfigs.labels.provider")}
                                                        </Text>
                                                        <Text size="sm">{step.ocrStepConfig.provider}</Text>
                                                    </Box>
                                                    <Box className="flow-diagram__step-info">
                                                        <Text size="xs" c="dimmed">
                                                            Model
                                                        </Text>
                                                        <Text size="sm">{step.ocrStepConfig.modelName}</Text>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                        {hasAgents && (
                                            <Badge
                                                variant="light"
                                                color="violet"
                                                size="lg"
                                                leftSection={<IconUsers size={14} />}
                                                className="flow-diagram__agent-badge"
                                            >
                                                {agents.length} {t("admin.flowConfigs.agents.agentCount")}
                                            </Badge>
                                        )}
                                        <Text size="xs" c="dimmed" ta="center" fs="italic" mt="xs">
                                            Click to edit
                                        </Text>
                                    </Paper>

                                    {hasAgents && (
                                        <Box className="flow-diagram__agents-section">
                                            <svg
                                                className="flow-diagram__curves-svg"
                                                viewBox={`0 0 100 ${agents.length * 56}`}
                                                preserveAspectRatio="none"
                                            >
                                                {agents.map((_, i) => {
                                                    const totalHeight = agents.length * 56;
                                                    const startY = totalHeight / 2;
                                                    const endY = i * 56 + 28;
                                                    return (
                                                        <path
                                                            key={i}
                                                            d={`M 0,${startY} C 50,${startY} 50,${endY} 100,${endY}`}
                                                            fill="none"
                                                            stroke="var(--mantine-color-violet-3)"
                                                            strokeWidth="2"
                                                        />
                                                    );
                                                })}
                                            </svg>
                                            <Box className="flow-diagram__agents-list">
                                                {agents.map((agent) => (
                                                    <Paper
                                                        key={agent.domainKey}
                                                        className="flow-diagram__agent-card"
                                                        onClick={() => handleAgentCardClick(agent, step)}
                                                    >
                                                        <IconRobot size={18} color="var(--mantine-color-violet-6)" />
                                                        <Text fw={600} size="sm">
                                                            {agent.domainKey}
                                                        </Text>
                                                    </Paper>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {index < sortedSteps.length - 1 && (
                                        <IconArrowRight
                                            className="flow-diagram__arrow"
                                            size={32}
                                            color="gray"
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {selectedStep && (
                <StepEditDialog
                    open={isDialogOpen}
                    onClose={handleCloseDialog}
                    onSave={onRefresh}
                    step={selectedStep}
                    docTypeId={docTypeId}
                    flowConfig={flowConfig}
                    initialTab={dialogInitialTab}
                />
            )}

            {selectedAgent && selectedAgentStep && (
                <AgentDetailModal
                    opened={isAgentModalOpen}
                    onClose={handleCloseAgentModal}
                    agent={selectedAgent}
                    step={selectedAgentStep}
                    flowConfig={flowConfig}
                    docTypeId={docTypeId}
                    onSave={onRefresh}
                />
            )}

            <EvaluationResults
                doctypeId={docTypeId}
                flowBaseId={flowConfig.flowBaseId}
                flowVersion={flowConfig.version}
            />
        </Box>
    );
};
